<template>
    <div class="CreateEditRiskProfile">
        <RiskProfileHeader
            :risk-profile-name="localProfile.displayName"
            :country-code="localProfile.countryCode"
            :risk-profile-active="localProfile.active"
            @input="onHeaderInput"
            @back="onBackAction"
            @save="attemptSave"
        />
        <div class="CreateEditRiskProfile__content">
            <div class="CreateEditRiskProfile__requirementTypeColumn">
                <RequirementTypeList
                    :requirement-types="requirementTypesWithTemplates"
                    :expanded-requirement-types="expandedRequirementTypes"
                    :criteria-by-type="criteriaByRequirementType"
                    :has-deprecated-criteria-by-requirement-type="hasDeprecatedCriteriaByRequirementType"
                    :get-requirement-type-label="getRequirementTypeLabel"
                    :has-evaluation-rule="isEvaluationRuleRequirementTypesSelected"
                    :is-requirement-type-in-evaluation-rule="isRequirementTypeInEvaluationRule"
                    :is-reference-starts-with-is-invalid="isReferenceStartsWithIsInvalid"
                    :disable-evaluation-rule="disableEvaluationRule"
                    :templates-by-requirement-type="templatesByRequirementType"
                    :custom-properties="customProperties"
                    :country-code="riskProfile.countryCode"
                    :criteria-templates="criteriaTemplates"
                    :evaluation-rule-requirement-types="evaluationRuleRequirementTypes"
                    :show-validation-errors="startShowingValidationErrors"
                    @add="addRequirementType"
                    @delete="tryDeleteRequirementType"
                    @expand="updateExpandedRequirementTypes"
                    @toggle-evaluation-rule-modal="toggleEvaluationRuleModal"
                    @criteria-input="updateCriteria"
                    @reference-accessed="onReferenceAccessed"
                    @validity-change="onValidityChanged"
                />
            </div>
            <RiskProfileAssignment
                class="CreateEditRiskProfile__riskProfileAssignment"
                :assignment-rule="localProfile.assignmentRule"
                :custom-properties="customProperties"
                :is-loading-custom-properties="loadingCustomProperties"
                :is-collateral-profile="isCollateralProfile"
                :show-error="startShowingValidationErrors"
                :get-requirement-type-label="getRequirementTypeLabel"
                :requirement-types="requirementTypes"
                @update-assignment-rule="updateAssignmentRule"
            />
        </div>

        <EvaluationRuleModal
            v-if="showEvaluationRuleModal"
            :evaluation-rule-requirement-types="evaluationRuleRequirementTypes"
            :get-requirement-type-label="getRequirementTypeLabel"
            :criteria-by-requirement-type="criteriaByRequirementType"
            :is-evaluation-rule-requirement-types-selected="isEvaluationRuleRequirementTypesSelected"
            @save="onSaveEvaluationRule"
            @delete="showEvaluationRuleConfirmDeleteModal"
            @close="toggleEvaluationRuleModal"
        />
        <ConfirmationModal
            v-if="confirmationModalData.text"
            additional-class-name="CreateEditRiskProfile__confirmation-modal"
            title="Are you sure?"
            yes="Delete"
            no="Cancel"
            :destructive="true"
            @input="onConfirmDelete"
        >
            {{ confirmationModalData.text }}
        </ConfirmationModal>

        <RiskProfileChangesWarningModal
            v-if="showRiskProfileChangesWarningModal"
            :change-details="changeDetails"
            @revert="revertRiskProfileChanges"
            @save="confirmSave"
        />
    </div>
</template>

<script lang="ts">
    import { defineAsyncComponent, PropType } from 'vue';

    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { flatten, intersectionWith, isEqual, omit, omitBy, startCase } from 'lodash';
    import difference from 'lodash/difference';
    import differenceWith from 'lodash/differenceWith';
    import orderBy from 'lodash/orderBy';

    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { AssignmentRule, AssignmentRuleType } from '@evidentid/tprm-portal-lib/models/decisioning/AssignmentRule.model';
    import {
        Criterion, CriterionBase,
        CriterionInput,
        CriterionTemplate,
    } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import {
        RiskProfile,
        RiskProfileInput,
        RiskProfileType,
    } from '@evidentid/tprm-portal-lib/models/decisioning/RiskProfile.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import {
        TprmRequirementModel,
    } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementModel.model';
    import { toTprmRequirementType } from '@evidentid/tprm-portal-lib/utils/common/toTprmRequirementType';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import RequirementTypeList from '@/modules/decisioning-criteria/components/RequirementTypeList/RequirementTypeList.vue';
    import RiskProfileAssignment
        from '@/modules/decisioning-criteria/components/RiskProfileAssignment/RiskProfileAssignment.vue';
    import RiskProfileChangesWarningModal
        from '@/modules/decisioning-criteria/components/RiskProfileChangesWarningModal/RiskProfileChangesWarningModal.vue';
    import RiskProfileHeader
        from '@/modules/decisioning-criteria/components/RiskProfileHeader/RiskProfileHeader.vue';
    import { CustomPropertiesListStatus } from '@/modules/decisioning-criteria/store';
    import { requirementTypeFieldReferenceDelimiter, UiAssignmentRule } from '@/modules/decisioning-criteria/types';
    import {
        RISK_PROFILE_AUTO_ADD_UMBRELLA_CREATE_PROFILE_ID,
        isRiskProfileAutoAddUmbrellaDisabled,
        startDisablingRiskProfileAutoAddUmbrella,
        purgeRiskProfileAutoAddUmbrellaPendingItems,
    } from '@/modules/decisioning-criteria/utils/autoAddUmbrella';
    import {
        uiAssignmentRuleToDbRule,
        dbAssignmentRuleToUiRule,
    } from '@/modules/decisioning-criteria/utils/parseRulesLogic';
    import {
        editableRequirementCriterionToRiskProfileCriterion,
        editableRequirementCriteriaToRiskProfileCriteria,
        riskProfileCriteriaToEditableRequirementCriteria,
    } from '@/modules/decisioning-criteria/utils/requirementsEditorConverters';
    import {
        createDefaultEditableCriteriaForRequirementType,
        groupTemplatesByType,
        mapTemplatesByField,
        setEditableCriteriaVerifyState,
        tryAutoAddUmbrellaRequirementType,
    } from '@/modules/decisioning-criteria/utils/requirementsEditorState';
    import { groupCriteriaByType } from '@/modules/decisioning-criteria/utils/riskProfileCriteria';

    const EvaluationRuleModal = defineAsyncComponent(
        () => import('@/modules/decisioning-criteria/components/EvaluationRuleModal/EvaluationRuleModal.vue'),
    );
    const UMBRELLA_EXCESS_LIABILITY_REQUIREMENT = toTprmRequirementType('UMBRELLA_EXCESS_LIABILITY');

    type RequirementTypesWithDeprecatedCriteria = Partial<Record<TprmRequirementType, boolean>>;
    type RequirementTypeCriteriaMap = Partial<Record<TprmRequirementType, CriterionBase[]>>;

    interface ConfirmationModalData {
        text?: string;
        confirmDeleteFor?: 'coverageType' | 'evaluationRule';
        coverageType?: TprmRequirementType;
        isCoverageTypeInEvaluationRule?: boolean;
    }

    function extractRequirementTypeFromCombinedReference(combinedRef: string): TprmRequirementType | null {
        return combinedRef.split('-')[0] as TprmRequirementType || null;
    }

    function isCriteriaEqual(a: CriterionBase, b: CriterionBase) {
        return a.coverageType === b.coverageType && a.field === b.field;
    }

    @Component({
        emits: [ 'criteriaInput', 'profileInput', 'revert-risk-profile-changes', 'save', 'updateExpandedRequirementTypes' ],
        components: {
            ConfirmationModal,
            EvaluationRuleModal,
            RequirementTypeList,
            RiskProfileAssignment,
            RiskProfileChangesWarningModal,
            RiskProfileHeader,
        },
    })
    export default class CreateEditRiskProfile extends Vue {
        @Prop({ type: Object, default: null })
        private riskProfile!: RiskProfile | RiskProfileInput;

        @Prop({ type: Array as PropType<TprmRequirementType[]>, default: (): TprmRequirementType[] => [] })
        private requirementTypes!: TprmRequirementType[];

        @Prop({ type: Array, default: () => [] })
        private requirementModels!: TprmRequirementModel[];

        @Prop({ type: Array, default: () => [] })
        private criteriaTemplates!: CriterionTemplate[];

        @Prop({ type: Array, default: () => [] })
        private criteria!: Criterion[];

        @Prop({ type: Array, default: () => [] })
        private originalCriteria!: Criterion[];

        @Prop({ type: Array, default: () => [] })
        private expandedRequirementTypes!: TprmRequirementType[];

        @Prop({ type: Object as PropType<CustomPropertiesListStatus>, required: true })
        private customPropertiesStatus!: CustomPropertiesListStatus;

        @Prop({ type: Boolean as PropType<boolean>, default: false })
        private loadingCustomProperties!: boolean;

        @Prop({ type: Boolean as PropType<boolean>, default: false })
        private collateralEnabled!: boolean;

        private invalidReferences: string[] = [];
        private accessedMap: Record<string, boolean> = {};
        private criteriaTemplatesByRequirementType: Record<string, CriterionTemplate[]> =
            this.groupAndFilterCriteriaTemplates(this.criteriaTemplates);

        private criteriaByRequirementType: RequirementTypeCriteriaMap =
            groupCriteriaByType(this.criteria);

        private originalCriteriaByRequirementType: RequirementTypeCriteriaMap =
            groupCriteriaByType(this.originalCriteria);

        private localProfile: RiskProfile | RiskProfileInput = { ...this.riskProfile };

        private assignmentType: AssignmentRuleType | null = this.localProfile.assignmentRule?.type || null;
        private showEvaluationRuleModal: boolean = false;
        private confirmationModalData: ConfirmationModalData = {};
        private changeDetails: string[] = [];
        private showRiskProfileChangesWarningModal: boolean = false;
        private saveAsActive: boolean = true;
        private canAutoAddUmbrella: boolean = true;
        private startShowingValidationErrors: boolean = false;

        private get customProperties(): CustomProperty[] {
            return this.customPropertiesStatus.list;
        }

        private get profileIdForAutoUmbrella(): string {
            return 'id' in this.localProfile ? this.localProfile.id : RISK_PROFILE_AUTO_ADD_UMBRELLA_CREATE_PROFILE_ID;
        }

        private get requirementTypesWithDeprecatedCriteria(): RequirementTypesWithDeprecatedCriteria {
            return intersectionWith(
                this.criteriaTemplates,
                this.criteria,
                (a, b) => a.field === b.field && a.coverageType === b.coverageType,
            )
                .filter((x) => x.deprecated)
                .reduce((acc, x) => ({ ...acc, [x.coverageType]: true }), {});
        }

        private get requirementTypesWithTemplates(): string[] {
            // filter by this.requirementTypes due to it' a list filtered by country while templates isn't
            return Object.keys(this.criteriaTemplatesByRequirementType).filter(
                (requirementTypeWithTemplate) => this.requirementTypes.includes(
                    requirementTypeWithTemplate as TprmRequirementType,
                ),
            );
        }

        private get evaluationRuleRequirementTypes(): TprmRequirementType[] {
            return this.localProfile.evaluationRule?.coverageTypes || [];
        }

        private get isEvaluationRuleRequirementTypesSelected(): boolean {
            const [ coverageOne, coverageTwo ] = this.evaluationRuleRequirementTypes;
            return Boolean(coverageOne) && Boolean(coverageTwo);
        }

        private get disableEvaluationRule(): boolean {
            return this.localProfile.ccgType === RiskProfileType.collateral;
        }

        private get riskProfileType(): RiskProfileType | null {
            return this.collateralEnabled
                ? this.localProfile.ccgType || RiskProfileType.default
                : null;
        }

        private get isCollateralProfile(): boolean {
            return this.riskProfileType === RiskProfileType.collateral;
        }

        private get baselineFields(): string[] {
            return [
                'carrier.amBestFinancialSizeCategory',
                'carrier.amBestFinancialStrengthRating',
                'carrier.naicNumber',
                'carrier.name',
                'coverage.certificateIssueDate',
                'coverage.coverageStatus',
                'coverage.endorsements.followFormBasis',
                'coverage.policiesFollowingForm',
                'coverage.producerName',
                'policy.currency',
                'policy.effectiveDate',
                'policy.expirationDate',
                'policy.insured',
                'policy.policyNumber',
            ];
        }

        private get rpName(): string {
            return this.$rp.current!;
        }

        private get isEditingProfile(): boolean {
            return Boolean('id' in this.localProfile && this.localProfile.id);
        }

        private get hasUmbrellaCriterion(): boolean {
            return this.criteria
                .some((criterion) => criterion.coverageType === UMBRELLA_EXCESS_LIABILITY_REQUIREMENT);
        }

        private get templatesByRequirementType(): Record<string, Record<string, CriterionTemplate>> {
            const result: Record<string, Record<string, CriterionTemplate>> = {};

            Object.keys(this.criteriaTemplatesByRequirementType).forEach((requirementType) => {
                result[requirementType] = this.criteriaTemplatesByRequirementType[requirementType]
                    .reduce((acc, temp) => ({ ...acc, [temp.field]: temp }), {});
            });
            return result;
        }

        private get isValidAssignmentRule(): boolean {
            if (this.localProfile.assignmentRule == null) {
                return false;
            }
            const validAll = this.localProfile.assignmentRule?.type === AssignmentRuleType.all;
            const validSingle = this.localProfile.assignmentRule?.type === AssignmentRuleType.single &&
                Boolean(this.localProfile.assignmentRule?.entityId);
            const validSelected = this.localProfile.assignmentRule?.type === AssignmentRuleType.selected &&
                Boolean(this.localProfile.assignmentRule?.expression);
            return validAll || validSingle || validSelected;
        }

        private get hasAnyError(): boolean {
            return !this.localProfile.displayName ||
                // both undefined(no option selected) and null(select entities with no rules) are errors
                !this.isValidAssignmentRule ||
                this.invalidReferences.length > 0;
        }

        private getRiskProfileChangeWarningDetails(): string[] {
            const newTypes = difference(
                Object.keys(this.criteriaByRequirementType),
                Object.keys(this.originalCriteriaByRequirementType),
            ) as TprmRequirementType[];
            const newCriteria =
                this.getNewCriteriaOfExistedRequirementTypes(newTypes, this.originalCriteria, this.criteria);
            const newCriteriaStrings = this.convertCriteriaToChangeStrings(newCriteria, this.criteriaTemplates);
            const newRequirementTypeString = newTypes.map(
                (type) => `${this.getRequirementTypeLabel(type as TprmRequirementType)} - New Requirement Added`,
            );
            return orderBy([ ...newRequirementTypeString, ...newCriteriaStrings ]);
        }

        @Watch('riskProfile')
        private onProfileChange(current: RiskProfile, previous: RiskProfile): void {
            if (!isEqual(current, previous)) {
                this.localProfile = { ...this.riskProfile };
                this.assignmentType = this.localProfile.assignmentRule?.type || null;
                this.criteriaTemplatesByRequirementType = this.groupAndFilterCriteriaTemplates(this.criteriaTemplates);
                this.criteriaByRequirementType = groupCriteriaByType(this.criteria);
            }
        }

        @Watch('criteriaTemplates')
        private onTemplateChange(
            current: CriterionTemplate[],
            previous: CriterionTemplate[],
        ): void {
            if (!isEqual(current, previous)) {
                this.criteriaTemplatesByRequirementType = this.groupAndFilterCriteriaTemplates(this.criteriaTemplates);
            }
        }

        @Watch('criteria')
        private onCriteriaChange(current: Criterion[], previous: Criterion[]): void {
            if (!isEqual(current, previous)) {
                this.criteriaByRequirementType = groupCriteriaByType(this.criteria);
            }
        }

        @Watch('originalCriteria')
        private onOriginalCriteriaChange(
            current: Criterion[],
            previous: Criterion[],
        ): void {
            if (!isEqual(current, previous)) {
                this.originalCriteriaByRequirementType = groupCriteriaByType(this.originalCriteria);
            }
        }

        @Watch('requirementTypes', { immediate: true })
        private onTypesChange(): void {
            // Remove any expanded requirement types that are no longer available
            const validExpandedTypes = this.expandedRequirementTypes.filter((type) =>
                this.requirementTypes.includes(type),
            );
            if (validExpandedTypes.length !== this.expandedRequirementTypes.length) {
                this.$emit('updateExpandedRequirementTypes', validExpandedTypes);
            }
        }

        @Watch('expandedRequirementTypes', { immediate: true })
        private onExpandedRequirementTypesChange(): void {
            // Initialize criteria for any new expanded requirement types
            this.expandedRequirementTypes.forEach((requirementType) => {
                if (!this.criteriaByRequirementType[requirementType]) {
                    // FIXME: we need an error handling for this case when coming from deeplink
                    this.criteriaByRequirementType[requirementType] =
                        this.getCriteriaTemplatesForNewType(requirementType);
                }
            });
        }

        @Watch('collateralEnabled', { immediate: true })
        private onCollateralEnabledChange(): void {
            // update criteria due to collateral enabling flag is async by api, it should be updated when done
            this.criteriaTemplatesByRequirementType = this.groupAndFilterCriteriaTemplates(this.criteriaTemplates);
        }

        private created(): void {
            purgeRiskProfileAutoAddUmbrellaPendingItems();
            this.canAutoAddUmbrella = !isRiskProfileAutoAddUmbrellaDisabled(this.profileIdForAutoUmbrella);
        }

        private isReferenceStartsWithIsInvalid(searchString: string): boolean {
            // Split by delimiter to avoid partial field name matches
            // (e.g., generalAggregateLimit vs generalAggregateLimitPer)
            const searchSegments = searchString.split(requirementTypeFieldReferenceDelimiter);

            return this.invalidReferences.some((reference) => {
                // Split reference into segments by the delimiter
                const refSegments = reference.split(requirementTypeFieldReferenceDelimiter);

                // If search has more segments than reference, it can't be a match
                if (searchSegments.length > refSegments.length) {
                    return false;
                }

                // Check if all search segments exist in reference segments in order
                return searchSegments.every((searchSegment, index) => refSegments[index] === searchSegment);
            });
        }

        private onBackAction() {
            this.$router.push({
                name: 'decisioning',
                params: {
                    rpId: this.$route.params.rpId,
                },
            }).catch((err) => {
                // ignoring the navigation abort "error" as it should not be an error and should not pollute console
                if (!/Navigation aborted from .+ via a navigation guard/.test(err)) {
                    console.error(err);
                }
            });
        }

        private onHeaderInput(name: string): void {
            this.localProfile = { ...this.localProfile, displayName: name };
            this.$emit('profileInput', this.localProfile);
        }

        private updateAssignmentRule(assignmentRule: AssignmentRule | null): void {
            this.localProfile = {
                ...this.localProfile,
                assignmentRule,
            };
        }

        private attemptSave(data: { isActive: boolean }): void {
            this.startShowingValidationErrors = true;
            // Check for validation errors after enabling error display
            if (this.hasAnyError) {
                // Don't proceed with save if there are validation errors
                return;
            }

            if (this.isEditingProfile &&
                this.localProfile.numberOfInsuredsAssigned &&
                this.localProfile.numberOfInsuredsAssigned > 0
            ) {
                this.changeDetails = this.getRiskProfileChangeWarningDetails();
                this.saveAsActive = data.isActive;
                if (this.changeDetails.length > 0) {
                    this.showRiskProfileChangesWarningModal = true;
                } else {
                    this.saveProfile(data);
                }
            } else {
                this.saveProfile(data);
            }
        }

        private filterEmptyRules(rule: UiAssignmentRule | null): UiAssignmentRule | null {
            if (rule?.type === AssignmentRuleType.selected) {
                const filteredRules = rule.expression.map((basicRule) => basicRule
                    .filter((rule) => rule.insuredFieldKey.length > 0 && rule.operator.length > 0),
                )
                    .filter((basicRule) => basicRule.length > 0);
                return {
                    ...rule,
                    expression: filteredRules,
                };
            }
            return rule;
        }

        private saveProfile(data: { isActive: boolean }): void {
            const filteredRule = this.localProfile.assignmentRule
                ? this.filterEmptyRules(dbAssignmentRuleToUiRule(this.localProfile.assignmentRule))
                : null;
            const encodedRules = uiAssignmentRuleToDbRule(filteredRule, this.customProperties);

            const riskProfile = {
                ...this.localProfile,
                assignmentRule: encodedRules,
                active: data.isActive,
                numberOfCoverageTypes: Object.keys(this.criteriaByRequirementType).length,
            };
            const criteriaList = flatten(Object.values(this.criteriaByRequirementType));
            this.$emit('save', { riskProfile, criteria: criteriaList });
        }

        private updateExpandedRequirementTypes(expandedTypes: TprmRequirementType[]): void {
            this.$emit('updateExpandedRequirementTypes', expandedTypes);
        }

        private addRequirementType(type: TprmRequirementType): void {
            const criteria = this.getCriteriaTemplatesForNewType(type);
            this.criteriaByRequirementType[type] = criteria;
            this.tryAutoAddUmbrella(criteria, [], type);
            this.$emit('criteriaInput', flatten(Object.values(this.criteriaByRequirementType)));

            // Auto-expand the newly added requirement type
            if (!this.expandedRequirementTypes.includes(type)) {
                this.updateExpandedRequirementTypes([ ...this.expandedRequirementTypes, type ]);
            }
        }

        private getCriteriaTemplatesForNewType(type: TprmRequirementType): CriterionInput[] {
            const templatesByType = groupTemplatesByType(this.criteriaTemplatesByRequirementType[type] || []);
            const templatesByField = mapTemplatesByField(templatesByType);
            const defaultCriteria = createDefaultEditableCriteriaForRequirementType(type, templatesByType);
            return defaultCriteria.map((criterion) => {
                const template = templatesByField[type]?.[criterion.field];
                const templateReferences = template?.evaluator.references || {};
                const result = editableRequirementCriterionToRiskProfileCriterion(
                    criterion, templateReferences,
                ) as CriterionInput;
                if (template?.displayMetadata) {
                    result.displayMetadata = template.displayMetadata;
                }
                return result;
            }) as CriterionInput[];
        }

        private updateCriteria(criteria: Criterion[], requirementType: TprmRequirementType): void {
            const originCriteria = this.criteriaByRequirementType[requirementType] || [];
            this.criteriaByRequirementType = {
                ...this.criteriaByRequirementType,
                [requirementType]: criteria,
            };
            this.tryAutoAddUmbrella(criteria, originCriteria, requirementType);
            this.$emit('criteriaInput', flatten(Object.values(this.criteriaByRequirementType)));
        }

        private tryDeleteRequirementType(requirementType: TprmRequirementType) {
            if (!this.isRequirementTypeInEvaluationRule(requirementType)) {
                this.deleteRequirementType(requirementType);
                return;
            }

            this.confirmationModalData = {
                text: `This requirement is currently assigned to an Evaluation Rule.
                Entities currently assigned to this risk profile may be affected.`,
                confirmDeleteFor: 'coverageType',
                coverageType: requirementType,
                isCoverageTypeInEvaluationRule: true,
            };
        }

        private deleteRequirementType(type: TprmRequirementType): void {
            this.criteriaByRequirementType = omit(this.criteriaByRequirementType, type);
            this.clearInvalidListPerRequirementType(type);

            // Remove from expanded list if present
            if (this.expandedRequirementTypes.includes(type)) {
                const updatedExpanded = this.expandedRequirementTypes.filter((expandedType) => expandedType !== type);
                this.updateExpandedRequirementTypes(updatedExpanded);
            }

            if (type === UMBRELLA_EXCESS_LIABILITY_REQUIREMENT) {
                startDisablingRiskProfileAutoAddUmbrella(this.profileIdForAutoUmbrella);
                this.canAutoAddUmbrella = false;
            }
            this.$emit('criteriaInput', flatten(Object.values(this.criteriaByRequirementType)));
        }

        private setCollectOnlyForCriteria(
            criteria: CriterionInput[],
        ): CriterionInput[] {
            return editableRequirementCriteriaToRiskProfileCriteria(
                setEditableCriteriaVerifyState(
                    riskProfileCriteriaToEditableRequirementCriteria(criteria),
                    false,
                ),
            ) as CriterionInput[];
        }

        private tryAutoAddUmbrella(
            criteria: (Criterion | CriterionInput)[],
            originalCriteria: CriterionBase[],
            requirementType: TprmRequirementType,
        ) {
            const editableCriteriaByRequirementType = Object.entries(this.criteriaByRequirementType)
                .reduce((acc, [ type, mappedCriteria ]) => ({
                    ...acc,
                    [type]: riskProfileCriteriaToEditableRequirementCriteria(mappedCriteria || []),
                }), {});
            const umbrellaTemplatesByType = groupTemplatesByType(
                this.criteriaTemplatesByRequirementType[UMBRELLA_EXCESS_LIABILITY_REQUIREMENT] || [],
            );
            const updatedEditableCriteriaByType = tryAutoAddUmbrellaRequirementType({
                criteriaByRequirementType: editableCriteriaByRequirementType,
                requirementType,
                currentCriteria: riskProfileCriteriaToEditableRequirementCriteria(criteria),
                originalCriteria: riskProfileCriteriaToEditableRequirementCriteria(originalCriteria as Criterion[]),
                canAutoAddUmbrella: this.canAutoAddUmbrella,
                umbrellaRequirementType: UMBRELLA_EXCESS_LIABILITY_REQUIREMENT,
                umbrellaCriteria: setEditableCriteriaVerifyState(
                    createDefaultEditableCriteriaForRequirementType(
                        UMBRELLA_EXCESS_LIABILITY_REQUIREMENT,
                        umbrellaTemplatesByType,
                    ),
                    false,
                ),
            });

            if (!updatedEditableCriteriaByType[UMBRELLA_EXCESS_LIABILITY_REQUIREMENT]) {
                return;
            }

            const umbrellaTemplatesByField = mapTemplatesByField(umbrellaTemplatesByType);
            this.criteriaByRequirementType[UMBRELLA_EXCESS_LIABILITY_REQUIREMENT] =
                (updatedEditableCriteriaByType[UMBRELLA_EXCESS_LIABILITY_REQUIREMENT] || []).map((criterion) => {
                    const template = umbrellaTemplatesByField[UMBRELLA_EXCESS_LIABILITY_REQUIREMENT]?.[criterion.field];
                    const templateReferences = template?.evaluator.references || {};
                    const result = editableRequirementCriterionToRiskProfileCriterion(
                        criterion, templateReferences,
                    ) as Criterion;
                    if (template?.displayMetadata) {
                        (result as any).displayMetadata = template.displayMetadata;
                    }
                    return result;
                });
        }

        private onReferenceAccessed(data: { reference: string, isValid: boolean }): void {
            this.accessedMap[data.reference] = true;
            this.onValidityChanged(data);
        }

        private onValidityChanged(data: { reference: string, isValid: boolean }): void {
            const index = this.invalidReferences.indexOf(data.reference);
            const refStartsWithRegex = new RegExp(`^${data.reference}`);
            if (data.isValid) {
                this.invalidReferences = this.invalidReferences.filter((ref) => !refStartsWithRegex.test(ref));
            } else if (!data.isValid && index < 0) {
                this.invalidReferences = [ ...this.invalidReferences, data.reference ];
            }
        }

        private clearInvalidListPerRequirementType(requirementType: TprmRequirementType): void {
            this.invalidReferences = this.invalidReferences.filter((combinedReference) =>
                extractRequirementTypeFromCombinedReference(combinedReference) !== requirementType);
        }

        private hasDeprecatedCriteriaByRequirementType(requirementType: TprmRequirementType): boolean {
            return Boolean(this.requirementTypesWithDeprecatedCriteria[requirementType]);
        }

        private toggleEvaluationRuleModal() {
            this.showEvaluationRuleModal = !this.showEvaluationRuleModal;
        }

        private onSaveEvaluationRule({ requirementTypes }: { requirementTypes: TprmRequirementType[] }) {
            this.localProfile = {
                ...this.localProfile,
                evaluationRule: { coverageTypes: requirementTypes },
            };
            this.$emit('profileInput', this.localProfile);
        }

        private showEvaluationRuleConfirmDeleteModal(): void {
            this.confirmationModalData = {
                text: 'Entities currently assigned to this risk profile may be affected.',
                confirmDeleteFor: 'evaluationRule',
            };
        }

        private onConfirmDelete(
            confirmed: boolean,
        ) {
            const {
                confirmDeleteFor,
                coverageType: requirementType,
                isCoverageTypeInEvaluationRule,
            } = this.confirmationModalData;
            this.confirmationModalData = {};
            if (!confirmed) {
                return;
            }
            if (confirmDeleteFor === 'evaluationRule') {
                this.deleteEvaluationRule();
            } else {
                this.deleteRequirementType(requirementType as TprmRequirementType);
                if (isCoverageTypeInEvaluationRule) {
                    this.deleteEvaluationRule();
                }
            }
        }

        private deleteEvaluationRule(): void {
            this.localProfile = {
                ...this.localProfile,
                evaluationRule: undefined,
            };
            this.$emit('profileInput', this.localProfile);
        }

        private getRequirementTypeLabel(requirementType: TprmRequirementType): string {
            return this.requirementModels.find((model) => model.coverageType === requirementType)?.label
                || startCase(requirementType.toLowerCase());
        }

        private isRequirementTypeInEvaluationRule(requirementType: TprmRequirementType): boolean {
            return this.evaluationRuleRequirementTypes.includes(requirementType);
        }

        private groupAndFilterCriteriaTemplates(
            templates: CriterionTemplate[],
        ): Record<string, CriterionTemplate[]> {
            if (this.collateralEnabled && this.riskProfile.ccgType === RiskProfileType.collateral) {
                return omitBy(
                    groupCriteriaByType(templates),
                    (criteria) => !criteria.some((template) => template.field === 'coverage.collateral'),
                );
            }
            return groupCriteriaByType(templates.filter((template) => template.field !== 'coverage.collateral'));
        }

        private getNewCriteriaOfExistedRequirementTypes(
            newRequirementTypes: TprmRequirementType[],
            originalCriteria: Criterion[],
            currentCriteria: Criterion[],
        ): Criterion[] {
            const newCriteria = differenceWith(currentCriteria, originalCriteria, isCriteriaEqual);
            return newCriteria.filter(
                (criterion: Criterion) =>
                    !newRequirementTypes.includes(criterion.coverageType) &&
                    !this.baselineFields.includes(criterion.field),
            );
        }

        private convertCriteriaToChangeStrings(
            criteria: Criterion[],
            criteriaTemplates: CriterionTemplate[],
        ): string[] {
            const uniqIdTemplateMap: Record<string, CriterionTemplate> =
                criteriaTemplates.reduce(
                    (accu, template) => ({ ...accu, [`${template.coverageType}-${template.field}`]: template }),
                    {},
                );
            return criteria.map(
                (criterion: Criterion) => {
                    const template = uniqIdTemplateMap[`${criterion.coverageType}-${criterion.field}`];
                    const requirementTypeLabel = this.getRequirementTypeLabel(criterion.coverageType);
                    return template
                        ? `${requirementTypeLabel} - ${template.displayMetadata.title} Added`
                        : `${requirementTypeLabel} - ${startCase(criterion.field.split('.').pop())} Added`;
                },
            );
        }

        private revertRiskProfileChanges(): void {
            this.showRiskProfileChangesWarningModal = false;
            this.$emit('revert-risk-profile-changes');
        }

        private confirmSave(): void {
            this.saveProfile({ isActive: this.saveAsActive });
            this.showRiskProfileChangesWarningModal = false;
        }
    }
</script>
